import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Card, Divider, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { openFile } from 'core/utils/browser'

import { useConfigContext } from 'config/ui/ConfigContext'

import { DocumentType } from '../../domain/Document'
import { isLTLorVLTL, type Shipment } from '../../domain/Shipment'
import { formatDocumentSubtitle } from '../../presenters/documentPresenter'
import { Attachments } from '../Attachments'
import { useGetInspectionCertificates } from '../hooks/useGetInspectionCertificates'
import { useGetInvoices } from '../hooks/useGetInvoices'
import { useGetPOD } from '../hooks/useGetPOD'
import { useGetProofOfPickup } from '../hooks/useGetProofOfPickup'
import { useGetWeightCertificates } from '../hooks/useGetWeightCertificates'
import { useShipment } from '../hooks/useShipment'
import { CapitalizedTag } from '../ShipmentInfo.styles'
import { DocumentItem } from './DocumentItem'
import { DocumentSection } from './DocumentSection'
import UploadDocumentDialog from './UploadDocumentDialog'

export type DocumentCardProps = Readonly<{
  shipment: Shipment
}>

export function DocumentCard({ shipment }: DocumentCardProps) {
  const { fetchBOL, fetchRatecon, downloadingBOL, downloadingRatecon } = useShipment()
  const shipmentUUID = shipment.uuid
  const { invoices, fetchInvoices } = useGetInvoices(shipmentUUID)
  const { podList, fetchPOD } = useGetPOD(shipmentUUID)
  const { proofsOfPickup, fetchProofOfPickup } = useGetProofOfPickup(shipmentUUID)
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)

  const { weightCertificates, fetchWeightCertificates } = useGetWeightCertificates(
    shipmentUUID,
    isLTLorVLTL(shipment)
  )
  const { inspectionCertificates, fetchInspectionCertificates } = useGetInspectionCertificates(
    shipmentUUID,
    isLTLorVLTL(shipment)
  )

  const hasBlindBol = Boolean(shipment.blindBol ?? shipment.stops.some(stop => stop.blindBol))

  const handleDocumentDialogOpen = useCallback(() => {
    setDocumentDialogOpen(!documentDialogOpen)
  }, [documentDialogOpen])

  const configs = useConfigContext()
  const enableEmbeddedAudit = configs.enableEmbeddedAudit.toBoolean()
  const hasPendingDocuments = invoices.length === 0 || podList?.length === 0

  const enableProofOfPickup = configs.enableProofOfPickup.toBoolean()

  function handleViewInvoice(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewPOD(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewProofOfPickup(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewWeightCertificate(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewInspectionCertificate(fileURL: string) {
    openFile(fileURL)
  }

  const viewBOL = useCallback(async () => {
    try {
      const data = await fetchBOL(shipmentUUID)

      openFile(data)
    } catch {
      toast.error('Error downloading BOL. Please try again')
    }
  }, [shipmentUUID, fetchBOL])

  const viewRatecon = useCallback(async () => {
    try {
      const data = await fetchRatecon(shipmentUUID)

      openFile(data)
    } catch {
      toast.error('Error downloading Ratecon. Please try again')
    }
  }, [shipmentUUID, fetchRatecon])

  const onUploadDocument = useCallback(
    async (documentType: string) => {
      switch (documentType) {
        case DocumentType.POD:
          fetchPOD(shipmentUUID)
          break
        case DocumentType.Invoice:
          fetchInvoices(shipmentUUID)
          break
        case DocumentType.ProofOfPickup:
          fetchProofOfPickup(shipmentUUID)
          break
        case DocumentType.WeightCertificate:
          fetchWeightCertificates(shipmentUUID)
          break
        case DocumentType.InspectionCertificate:
          fetchInspectionCertificates(shipmentUUID)
          break
      }
    },
    [
      fetchPOD,
      shipmentUUID,
      fetchInvoices,
      fetchWeightCertificates,
      fetchInspectionCertificates,
      fetchProofOfPickup,
    ]
  )

  return (
    <>
      <Card data-testid="document-card" collapsible>
        <Card.Title>
          <Layout.Group>
            <Text variant="heading-sm-bold">Documents</Text>
            {hasPendingDocuments && (
              <CapitalizedTag variant="warning">Pending Documents</CapitalizedTag>
            )}
          </Layout.Group>
        </Card.Title>
        <Card.Divider />
        <Card.Body>
          <Layout.Stack>
            <DocumentSection name="invoices">
              {invoices.map(invoice => (
                <DocumentItem
                  key={invoice.createdAt}
                  title={invoice.total}
                  createdAt={invoice.createdAt}
                  type={DocumentType.Invoice}
                  onClick={() => handleViewInvoice(invoice.url)}
                  status={invoice.status}
                  auditEnabled={enableEmbeddedAudit}
                />
              ))}
            </DocumentSection>
            <Divider />
            <DocumentSection name="rate confirmation">
              <DocumentItem
                title={shipment?.spotQuote?.bookedRate?.flatRate}
                type={DocumentType.RateConfirmation}
                onClick={viewRatecon}
                loading={downloadingRatecon}
                auditEnabled={enableEmbeddedAudit}
              />
            </DocumentSection>
            <Divider />
            <DocumentSection name="bol">
              <DocumentItem
                title={shipment?.bolNumber}
                type={DocumentType.BOL}
                onClick={viewBOL}
                loading={downloadingBOL}
                auditEnabled={enableEmbeddedAudit}
                subtitle={hasBlindBol ? 'Blind BOL' : undefined}
              />
            </DocumentSection>
            <Divider />
            {shipment.spotQuote?.bookedRate && (
              <Attachments
                rateUuid={shipment.spotQuote.bookedRate.uuid}
                attachments={shipment.spotQuote.attachments}
                quoteUuid={shipment.spotQuote.uuid}
              />
            )}
            {shipment.spotQuote && shipment.spotQuote.attachments.length > 0 && <Divider />}
            <DocumentSection name="proof of delivery">
              {podList.map(pod => (
                <DocumentItem
                  key={pod.createdAt}
                  title={pod.name}
                  type={DocumentType.POD}
                  onClick={() => handleViewPOD(pod.url)}
                  auditEnabled={enableEmbeddedAudit}
                  subtitle={formatDocumentSubtitle(pod, shipment.stops, 'Delivery')}
                />
              ))}
            </DocumentSection>
            <Divider />
            {enableProofOfPickup && (
              <DocumentSection name="proof of pickup">
                {proofsOfPickup.map(proofOfPickup => (
                  <DocumentItem
                    key={proofOfPickup.createdAt}
                    title={proofOfPickup.name}
                    type={DocumentType.ProofOfPickup}
                    onClick={() => handleViewProofOfPickup(proofOfPickup.url)}
                    auditEnabled={enableEmbeddedAudit}
                    subtitle={formatDocumentSubtitle(proofOfPickup, shipment.stops, 'Pick up')}
                  />
                ))}
              </DocumentSection>
            )}
            {isLTLorVLTL(shipment) && (
              <>
                <Divider />
                <DocumentSection name="weight certificate">
                  {weightCertificates?.map(weightCertificate => (
                    <DocumentItem
                      key={weightCertificate.createdAt}
                      title={weightCertificate.name}
                      createdAt={weightCertificate.createdAt}
                      type={DocumentType.WeightCertificate}
                      onClick={() => handleViewWeightCertificate(weightCertificate.url)}
                      auditEnabled={enableEmbeddedAudit}
                    />
                  ))}
                </DocumentSection>
                <Divider />
                <DocumentSection name="inspection certificate">
                  {inspectionCertificates?.map(inspectionCertificate => (
                    <DocumentItem
                      key={inspectionCertificate.createdAt}
                      title={inspectionCertificate.name}
                      createdAt={inspectionCertificate.createdAt}
                      type={DocumentType.InspectionCertificate}
                      onClick={() => handleViewInspectionCertificate(inspectionCertificate.url)}
                      auditEnabled={enableEmbeddedAudit}
                    />
                  ))}
                </DocumentSection>
              </>
            )}
          </Layout.Stack>
        </Card.Body>
        <Card.Footer align="flex-start">
          <Button
            size="small"
            leading={<Icon name="upload" size="small" />}
            onClick={handleDocumentDialogOpen}
            data-testid="upload-document-button"
          >
            Upload
          </Button>
        </Card.Footer>
      </Card>
      <UploadDocumentDialog
        shipmentUUID={shipmentUUID}
        isLTLorVLTL={isLTLorVLTL(shipment)}
        open={documentDialogOpen}
        hide={handleDocumentDialogOpen}
        onUploadDocument={onUploadDocument}
        stops={shipment.stops}
      />
    </>
  )
}
